import React, { useMemo, useState } from 'react';
import { getLostParentChain } from '../Sidebar/utils/orphanDetection';
import './OrphanWarningBanner.css';

/**
 * Shown at the top of a campaign's detail view when that campaign points at a
 * parent that can no longer be found. The campaign is NOT auto-modified — we
 * surface it here and let the user decide what to do.
 *
 * Also renders the breadcrumb path the campaign used to live in, marking the
 * exact ancestor that's missing (the break point) for debugging.
 */
function OrphanWarningBanner({ sprint, allSprints = [], onMakeTopLevel, onDelete }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const chain = useMemo(
    () => getLostParentChain(sprint, allSprints),
    [sprint, allSprints]
  );

  if (!sprint) return null;

  const missing = chain.find((c) => !c.exists);

  return (
    <div className="orphan-banner" role="alert">
      <div className="orphan-banner__body">
        <span className="orphan-banner__icon" aria-hidden>⚠</span>
        <div className="orphan-banner__text">
          <div className="orphan-banner__title">
            This campaign was nested, but its parent can no longer be found.
          </div>

          <div className="orphan-banner__path">
            <span className="orphan-banner__path-label">Was nested under:</span>
            {chain.length === 0 ? (
              <span className="orphan-banner__crumb orphan-banner__crumb--missing">
                {sprint.parentSprintId || 'unknown'} (missing)
              </span>
            ) : (
              chain.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i > 0 && <span className="orphan-banner__sep">›</span>}
                  <span
                    className={
                      'orphan-banner__crumb' +
                      (!c.exists ? ' orphan-banner__crumb--missing' : '') +
                      (c.deleted ? ' orphan-banner__crumb--trashed' : '')
                    }
                    title={c.id}
                  >
                    {c.exists ? c.name : `${c.id} (missing)`}
                    {c.deleted ? ' (in trash)' : ''}
                  </span>
                </React.Fragment>
              ))
            )}
            <span className="orphan-banner__sep">›</span>
            <span className="orphan-banner__crumb orphan-banner__crumb--current">
              {sprint.name}
            </span>
          </div>

          {missing && (
            <div className="orphan-banner__hint">
              Broken link at: <code>{missing.id}</code>
            </div>
          )}
        </div>
      </div>

      <div className="orphan-banner__actions">
        {confirmingDelete ? (
          <>
            <span className="orphan-banner__confirm-text">Move to trash?</span>
            <button
              type="button"
              className="orphan-banner__btn orphan-banner__btn--danger"
              onClick={() => onDelete?.(sprint.id)}
            >
              Yes, delete
            </button>
            <button
              type="button"
              className="orphan-banner__btn"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="orphan-banner__btn orphan-banner__btn--primary"
              onClick={() => onMakeTopLevel?.(sprint)}
              title="Clear the missing-parent link and keep this as a normal top-level campaign"
            >
              Keep at top level
            </button>
            <button
              type="button"
              className="orphan-banner__btn orphan-banner__btn--danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete campaign
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default OrphanWarningBanner;
